import { Copy, Globe, Play, RotateCw, Square, Trash2 } from 'lucide-react';
import type { AssetRuntime, AssetRuntimeType } from '../../types/assetRuntime';
import { assetTypeLabel } from '../../api/agentAssetRuntimes';
import { useToast } from '../../components/ui/toastContext';
import { copyToClipboard } from './utils';

export interface AssetRuntimeToolbarProps {
  /** 工具栏对应的资产类型（用于过滤该类型的在线服务）。 */
  assetType: AssetRuntimeType;
  /** 当前 Agent 的全部资产在线运行时，内部按 assetType 过滤。 */
  runtimes: AssetRuntime[];
  /** 运行时列表加载中（禁用部署按钮）。 */
  loading: boolean;
  onDeploy: (assetType: AssetRuntimeType) => void;
  onStart: (rt: AssetRuntime) => void;
  onStop: (rt: AssetRuntime) => void;
  onRestart: (rt: AssetRuntime) => void;
  onDelete: (rt: AssetRuntime) => void;
}

/**
 * 资产在线服务工具栏：展示某类资产的在线运行时列表，支持部署 / 启停 / 重启 / 删除 / 复制端点。
 * 所有操作经 props 回调委托页面执行，组件自身只负责展示与剪贴板交互。
 */
export default function AssetRuntimeToolbar({
  assetType,
  runtimes,
  loading,
  onDeploy,
  onStart,
  onStop,
  onRestart,
  onDelete,
}: AssetRuntimeToolbarProps) {
  const toast = useToast();
  const filtered = runtimes.filter((rt) => rt.asset_type === assetType);
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 style={{ margin: 0 }} className="text-muted">在线服务</h4>
        <button
          className="secondary"
          style={{ padding: '6px 12px', fontSize: 13 }}
          onClick={() => onDeploy(assetType)}
          disabled={loading}
        >
          <Globe size={14} /> 部署 {assetTypeLabel(assetType)} 在线服务
        </button>
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted text-sm">暂无在线服务，点击右上角部署</p>
      ) : (
        <div className="list">
          {filtered.map((rt) => (
            <div key={rt.id} className="list-item">
              <div className="list-item-content">
                <div className="flex items-center gap-2">
                  <strong>{assetTypeLabel(rt.asset_type)}</strong>
                  <span className={`badge ${rt.status === 'running' ? 'success' : rt.status === 'error' ? 'error' : 'info'}`}>
                    {rt.status}
                  </span>
                </div>
                {rt.endpoint_url && (
                  <div className="text-sm text-muted" style={{ fontFamily: 'monospace', marginTop: 4 }}>
                    {rt.endpoint_url}
                  </div>
                )}
                {rt.error_message && (
                  <div className="text-sm" style={{ color: 'var(--color-error)', marginTop: 4 }}>
                    {rt.error_message}
                  </div>
                )}
              </div>
              <div className="list-item-actions">
                {rt.endpoint_url && (
                  <button
                    className="secondary"
                    style={{ padding: '6px 10px' }}
                    title="复制端点"
                    onClick={async () => {
                      const ok = await copyToClipboard(rt.endpoint_url!);
                      if (ok) {
                        toast.success('端点已复制');
                      } else {
                        toast.error('复制失败');
                      }
                    }}
                  >
                    <Copy size={14} />
                  </button>
                )}
                {rt.status === 'running' ? (
                  <button className="secondary" style={{ padding: '6px 10px' }} onClick={() => onStop(rt)} title="停止">
                    <Square size={14} />
                  </button>
                ) : (
                  <button className="secondary" style={{ padding: '6px 10px' }} onClick={() => onStart(rt)} title="启动">
                    <Play size={14} />
                  </button>
                )}
                <button className="secondary" style={{ padding: '6px 10px' }} onClick={() => onRestart(rt)} title="重启">
                  <RotateCw size={14} />
                </button>
                <button className="danger" style={{ padding: '6px 10px' }} onClick={() => onDelete(rt)} title="删除">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
